import React from 'react';
import PublicNav from '../../components/layout/PublicNav';

/**
 * Data Retention and Disposal Policy Component
 * 
 * Displays the company's data retention and disposal practices
 */
const DataRetentionPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6C5DD3] to-[#8247E5]">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white bg-opacity-95 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden">
          <div className="p-8 md:p-12">
            <PublicNav showPolicyLinks />

            <h1 className="text-3xl font-bold text-[#6C5DD3] mb-6">Data Retention and Disposal Policy</h1>
            
            <div className="prose max-w-none text-gray-600">
              <p className="text-sm text-gray-500 mb-8">Last Updated: May 15, 2024</p>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Introduction</h2>
                <p>
                  O Industries LLC ("we", "our", or "us") is committed to responsible data management practices. 
                  This Data Retention and Disposal Policy outlines how we collect, store, and eventually dispose of 
                  personal and financial data when using MnyMny application and related services.
                </p>
                <p>
                  We maintain this policy to ensure compliance with applicable privacy laws and regulations 
                  while protecting your information throughout its lifecycle.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Scope</h2>
                <p>This policy applies to:</p>
                <ul className="list-disc ml-6 mb-4">
                  <li>All personal information collected through our service</li>
                  <li>Financial transaction data and account information</li>
                  <li>User-generated content including budgets, goals, and notes</li>
                  <li>Usage and analytics data</li>
                  <li>Backup and archived data</li>
                </ul>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Data Retention Periods</h2>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Account Information</h3>
                <p>
                  We retain your account information (name, email, phone number, etc.) for as long as you maintain 
                  an active account with us, and for a period of 24 months after account deactivation to facilitate
                  potential reactivation and service improvements.
                </p>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Transaction Data</h3>
                <p>
                  Financial transaction data is retained for 7 years from the date of transaction to support financial 
                  record-keeping, tax compliance, and to provide historical insights to users. This includes:
                </p>
                <ul className="list-disc ml-6 mb-4">
                  <li>Transaction amounts, dates, and categories</li>
                  <li>Merchant information</li>
                  <li>Account balances and statements</li>
                </ul>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">User-Generated Content</h3>
                <p>
                  Budgets, savings goals, and custom categories are retained for as long as your account 
                  remains active and for 24 months after account deactivation.
                </p>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Usage Data</h3>
                <p>
                  Information about how you interact with our service is retained for 24 months to help 
                  us improve our services and user experience.
                </p>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Communications</h3>
                <p>
                  Customer support communications and correspondence are retained for 3 years from the date 
                  of communication to maintain service quality and address ongoing concerns.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Data Disposal Methods</h2>
                <p>
                  When data reaches the end of its retention period, we employ the following disposal methods:
                </p>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Deletion</h3>
                <p>
                  Standard deletion procedures remove data from our active systems but may leave fragments in 
                  backup systems until those backups are cycled out.
                </p>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Anonymization</h3>
                <p>
                  Some data may be anonymized rather than deleted, meaning all personally identifiable elements 
                  are removed, allowing us to retain useful analytics data while protecting your privacy.
                </p>
                
                <h3 className="text-lg font-medium text-gray-700 mb-2">Secure Destruction</h3>
                <p>
                  When physical media containing user data (such as backup drives) reaches end-of-life, 
                  we ensure secure destruction in accordance with industry standards.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Your Data Control Rights</h2>
                <p>
                  You maintain control over your data throughout its lifecycle with us:
                </p>
                <ul className="list-disc ml-6 mb-4">
                  <li><strong>Early Deletion:</strong> You may request deletion of your account and associated data at any time</li>
                  <li><strong>Data Export:</strong> You can download your data in a machine-readable format</li>
                  <li><strong>Restriction of Processing:</strong> You may request limitations on how we use your data</li>
                </ul>
                <p>
                  To exercise these rights, contact us at privacy@oindustries.com or through your account settings.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Exceptions to Standard Retention</h2>
                <p>
                  In certain situations, we may need to retain data beyond the standard retention periods:
                </p>
                <ul className="list-disc ml-6 mb-4">
                  <li><strong>Legal Obligations:</strong> To comply with applicable laws, regulations, or court orders</li>
                  <li><strong>Dispute Resolution:</strong> To resolve active disputes or investigations</li>
                  <li><strong>Security Incidents:</strong> To investigate and remediate security incidents</li>
                  <li><strong>Service Improvements:</strong> Anonymized data may be retained indefinitely for service improvement</li>
                </ul>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Data Backup and Recovery</h2>
                <p>
                  To ensure service continuity and data integrity, we maintain regular backups of our systems:
                </p>
                <ul className="list-disc ml-6 mb-4">
                  <li>Daily backups are retained for 30 days</li>
                  <li>Weekly backups are retained for 3 months</li>
                  <li>Monthly backups are retained for 1 year</li>
                </ul>
                <p>
                  Even after you've deleted your data from active systems, it may persist in these backups until 
                  they expire and are overwritten as part of our normal backup rotation.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Policy Updates</h2>
                <p>
                  We may update this Data Retention and Disposal Policy periodically to reflect changes in our practices 
                  or legal requirements. We will notify users of significant changes via email or through the application.
                </p>
                <p>
                  The "Last Updated" date at the top of this policy indicates when it was last revised.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Contact Us</h2>
                <p>
                  If you have questions or concerns about our data retention practices, please contact us at:
                </p>
                <p className="mt-2">
                  O Industries LLC<br />
                  Email: privacy@oindustries.com<br />
                  Address: [Company Address]
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataRetentionPolicy; 